// ============================================
// ROUTE CONSTANTS
// ============================================

export const sidebarRoutes = {
  USERS: "users",
  DASHBOARD: "dashboard",
};

export const APP_ROUTES = {
  home: "home",
  users: "users",
  outputs: "outputs",
  patient_opportunity_mapping_and_strategy: "patient_opportunity_mapping_and_strategy",
  hcp_segmentation: "hcp_segmentation",
  hcp_insights: "hcp_insights",
  eligible_patient_locator: "eligible_patient_locator",
  geographic_unmet_need: "geographic_unmet_need",
  hcp_profiles: "hcp_profiles",
  lead_indicators: "lead_indicators",
  patient_journey: "patient_journey",
  impact_tracking: "impact_tracking",
  institutional_variation: "institutional_variation",
  payer_variation: "payer_variation",
  medical_affair_toolbox: "medical_affair_toolbox",
  data_quality: "data_quality",
  unmet_need_definition: "unmet_need_definition",
  target_lists: "target_lists",
  priority_engagement_opportunity_page: "priority_engagement_opportunity_page",
  help: "help",
  testing: "testing",
  settings: "settings",
};

export const APP_ROUTES_LABEL = {
  home: "Home",
  users: "Users",
  outputs: "Outputs",
  patient_opportunity_mapping_and_strategy: "Patient Opportunity and Unmet Need Strategy",
  hcp_segmentation: "HCP Segmentation",
  eligible_patient_locator: "Physician Care Profiles",
  geographic_unmet_need: "Geographic Unmet Need",
  hcp_insights: "HCP Insights",
  hcp_profiles: "HCP Profiles",
  lead_indicators: "Leading Indicators",
  patient_journey: "Asthma Patient Trajectory and Data Quality",
  impact_tracking: "Impact Tracking",
  institutional_variation: "Hospital/Clinic Care Profiles",
  payer_variation: "Payer Care Profiles",
  medical_affair_toolbox: "Medical Affairs Toolbox",
  target_lists: "Saved Accounts",
  data_quality: "Data Quality",
  unmet_need_definition: "Unmet Need Definitions",
  priority_engagement_opportunity_page: "Priority Engagement Opportunities",
  help: "Help",
  testing: "Testing",
  settings: "Settings",
};

// ============================================
// TABLE COLUMNS
// ============================================

export const EPL_TABLE_COLUMNS = [
  { Header: "Improper CV risk testing", accessor: "cvRisk" },
  { Header: "Improper calcium channel blocker", accessor: "calciumBlocker" },
  { Header: "Lack of monitoring by CV specialist", accessor: "monitoring" },
  { Header: "Off-label treatment High AF stroke risk without anticoagulant", accessor: "offLabelTreatment" },
  { Header: "Non-adherence to anticoagulants", accessor: "nonAdherenceAnticoagulants" },
  { Header: "Incomplete comorbidity testing Continued AF without treatment escalation", accessor: "incompleteTesting" },
  { Header: "Repeated cardioversions without treatment escalation", accessor: "repeatedCardioversions" },
  { Header: "Improper support of therapy", accessor: "supportTherapy" },
  { Header: "Failure to manage AEs", accessor: "manageAEs" },
  { Header: "Non-adherence to other AF drug treatments", accessor: "nonAdherenceAFDrugs" },
  { Header: "Failure to complete follow-up testing", accessor: "failureFollowUp" },
];

export const USERS_TABLE_COLUMNS = [
  { Header: "Email", accessor: "email" },
  { Header: "Region", accessor: "region" },
  { Header: "Company", accessor: "company" },
];

// ============================================
// PATIENT TOTALS (used for color coding)
// ============================================

export const patientTotals = [
  "Number of Asthma Patients",
  "Number of ICS Patients",
  "Number of ICS Exacerbation",
  "Number of ICS-LABA Patients",
  "Number of ICS-LABA Exacerbation",
  "Total Asthma Patients",
  "Total ICS Patients",
  "Total ICS Exacerbations",
  "Total ICS-LABA Patients",
  "Total ICS-LABA LAMA Patients",
  "Number of ICS-LABA LAMA Patients",
  "Number of ICS-LABA-LAMA Patients",
  "Number of Biologic Patients",
];

// ============================================
// UNMET NEEDS DEFINITIONS
// ============================================

export const unmetLabels = {
  "Incomplete initial asthma testing": { id: "Total No Spirometry", percent: "Percent No Spirometry" },
  "Incomplete severe asthma testing": { id: "Total No EOS Testing", percent: "Percent No EOS Testing" },
  "Exacerbation Prior to Receiving Single Therapy": { id: "Total No Treatment", percent: "Percent No Treatment" },
  "Failure to Escalate Uncontrolled / Severe Patients from Single Therapy": { id: "Total ICS Exacerbation Failed Escalation", percent: "Percent ICS Exacerbation Failed Escalation" },
  "Excessive Steroid Usage on Single Therapy": { id: "Total ICS High Steroid Usage", percent: "Percent ICS High Steroid Usage" },
  "Delay in Escalating Patients from Single to Double Therapy": { id: "Total ICS Escalation Delay", percent: "Percent ICS Escalation Delay" },
  "Failure to Escalate Uncontrolled / Severe Patients from Double Therapy": { id: "Total ICS-LABA High Steroid Usage", percent: "Percent ICS-LABA High Steroid Usage" },
  "Excessive Steroid Usage on Double Therapy": { id: "Total ICS-LABA Exacerbation Failed Escalation", percent: "Percent ICS-LABA Exacerbation Failed Escalation" },
  "Number of ICS-LABA >900mg/year steroids": { id: "Number of ICS-LABA >900mg/year steroids", percent: "Percent of ICS-LABA >900mg/year steroids" },
  "Number of ICS-LABA High Steroid Usage with ER visit": { id: "Number of ICS-LABA High Steroid Usage with ER visit", percent: "Percent of ICS-LABA High Steroid Usage with ER visit" },
  "Number of ICS-LABA High Steroid Usage without ER visit": { id: "Number of ICS-LABA High Steroid Usage without ER visit", percent: "Percent of ICS-LABA High Steroid Usage without ER visit" },
  "Delay in Escalating Patients from Double to Triple Therapy": { id: "Total ICS-LABA Escalation Delay", percent: "Percent ICS-LABA Escalation Delay" },
  "Use of open triple therapy": { id: "Total ICS-LABA With LAMA", percent: "Percent ICS-LABA With LAMA" },
  "Biologic before Triple Therapy": { id: "Number of Biologic Before Triple", percent: "Percent of Biologic Before Triple" },
  "Non-adherence to single therapy (persistence)": { id: "Total ICS Persistence < 360", percent: "Percent ICS Persistence < 360" },
  "Non-adherence to single therapy (compliance)": { id: "Total ICS Compliance < 240", percent: "Percent ICS-LABA Compliance < 240" },
  "Non-adherence to double therapy (persistence)": { id: "Total ICS-LABA Persistence < 360", percent: "Percent ICS-LABA Persistence < 360" },
  "Non-adherence to double therapy (compliance)": { id: "Total ICS-LABA Compliance < 240", percent: "Percent ICS-LABA Compliance < 240" },
  "Non-adherence to triple therapy (persistence)": { id: "Total ICS-LABA LAMA Persistence < 360", percent: "Percent ICS-LABA LAMA Persistence < 360" },
  "Non-adherence to triple therapy (compliance)": { id: "Total ICS-LABA LAMA Compliance < 240", percent: "Percent ICS-LABA LAMA Compliance < 240" },
};

// ============================================
// LABEL MAPPINGS - Single Source of Truth
// ============================================

// Primary label definitions: maps data keys to display labels
export const selectLabels = {
  // Patient counts
  "Number of Asthma Patients": "Number of Asthma Patients",
  "Number of ICS Patients": "Number of ICS Patients",
  "Number of ICS-LABA Patients": "Number of ICS-LABA Patients",
  "Number of ICS-LABA LAMA Patients": "Number of ICS-LABA-LAMA Patients",
  "Number of Biologic Patients": "Number of Biologic Patients",
  
  // Spirometry
  "Number of No Spirometry": "Incomplete initial asthma testing",
  "Percent of No Spirometry": "Incomplete initial asthma testing percent",
  "Number of Exacerbation Without Spirometry": "Exacerbation with no Spirometry",
  "Percent of Exacerbation Without Spirometry": "Exacerbation with no Spirometry percent",
  
  // EOS Testing
  "Number of No EOS Testing": "Incomplete severe asthma testing",
  "Percent of No EOS Testing": "Incomplete severe asthma testing percent",
  
  // No Treatment
  "Number of No Treatment": "Exacerbation Prior to Receiving Single Therapy",
  "Percent of No Treatment": "Exacerbation Prior to Receiving Single Therapy percent",
  
  // ICS (Single Therapy)
  "Number of ICS High Steroid Usage": "Excessive Steroid Usage on Single Therapy",
  "Percent of ICS High Steroid Usage": "Excessive Steroid Usage on Single Therapy percent",
  "Number of ICS Exacerbation Failed Escalation": "Failure to Escalate Uncontrolled / Severe Patients from Single Therapy",
  "Percent of ICS Exacerbation Failed Escalation": "Failure to Escalate Uncontrolled / Severe Patients from Single Therapy percent",
  "Number of ICS Escalation Delay": "Delay in Escalating Patients from Single to Double Therapy",
  "Percent of ICS Escalation Delay": "Delay in Escalating Patients from Single to Double Therapy percent",
  "Number of ICS Persistence < 360": "Non-adherence to single therapy (persistence)",
  "Percent of ICS Persistence < 360": "Non-adherence to single therapy (persistence) percent",
  "Number of ICS Compliance < 240": "Non-adherence to single therapy (compliance)",
  "Percent of ICS Compliance < 240": "Non-adherence to single therapy (compliance) percent",
  
  // ICS-LABA (Double Therapy)
  "Number of ICS-LABA High Steroid Usage": "Failure to Escalate Uncontrolled / Severe Patients from Double Therapy",
  "Percent of ICS-LABA High Steroid Usage": "Failure to Escalate Uncontrolled / Severe Patients from Double Therapy percent",
  "Number of Failure To Escalate With 3 Exacerbations": "Failure to Escalate 3+ Exacerbations on Double Therapy",
  "Percent of Failure To Escalate With 3 Exacerbations": "Failure to Escalate 3+ Exacerbations on Double Therapy percent",
  "Number of ICS-LABA Exacerbation Failed Escalation": "Excessive Steroid Usage on Double Therapy",
  "Percent of ICS-LABA Exacerbation Failed Escalation": "Excessive Steroid Usage on Double Therapy percent",
  "Number of ICS-LABA >900mg/year steroids": ">900mg/year steroids on Double Therapy",
  "Percent of ICS-LABA >900mg/year steroids": ">900mg/year steroids on Double Therapy percent",
  "Number of ICS-LABA High Steroid Usage with ER visit": "Excessive Steroids with <5mg/OCS day on Double Therapy",
  "Percent of ICS-LABA High Steroid Usage with ER visit": "Excessive Steroids with <5mg/OCS day on Double Therapy percent",
  "Number of ICS-LABA High Steroid Usage without ER visit": "Excessive steroids with >5mg/OCS day on Double Therapy",
  "Percent of ICS-LABA High Steroid Usage without ER visit": "Excessive steroids with >5mg/OCS day on Double Therapy percent",
  "Number of ICS-LABA Escalation Delay": "Delay in Escalating Patients from Double to Triple Therapy",
  "Percent of ICS-LABA Escalation Delay": "Delay in Escalating Patients from Double to Triple Therapy percent",
  "Number of ICS-LABA Persistence < 360": "Non-adherence to double therapy (persistence)",
  "Percent of ICS-LABA Persistence < 360": "Non-adherence to double therapy (persistence) percent",
  "Number of ICS-LABA Compliance < 240": "Non-adherence to double therapy (compliance)",
  "Percent of ICS-LABA Compliance < 240": "Non-adherence to double therapy (compliance) percent",
  
  // ICS-LABA LAMA (Triple Therapy)
  "Number of ICS-LABA Patients with LAMA": "Use of open triple therapy",
  "Percent of ICS-LABA Patients with LAMA": "Use of open triple therapy percent",
  "Number of Biologic Before Triple": "Biologic before Triple Therapy",
  "Percent of Biologic Before Triple": "Biologic before Triple Therapy percent",
  "Number of ICS-LABA LAMA Persistence < 360": "Non-adherence to triple therapy (persistence)",
  "Percent of ICS-LABA LAMA Persistence < 360": "Non-adherence to triple therapy (persistence) percent",
  "Number of ICS-LABA LAMA Compliance < 240": "Non-adherence to triple therapy (compliance)",
  "Percent of ICS-LABA LAMA Compliance < 240": "Non-adherence to triple therapy (compliance) percent",
};

// Alias for backward compatibility
export const mapSelectLabels = selectLabels;

// Maps "Total" format keys to "Number of" format keys
export const mapLabels = {
  "Total Asthma Patients": "Number of Asthma Patients",
  "Total No Spirometry": "Number of No Spirometry",
  "Percent No Spirometry": "Percent of No Spirometry",
  "Total No EOS Testing": "Number of No EOS Testing",
  "Percent No EOS Testing": "Percent of No EOS Testing",
  "Total No Treatment": "Number of No Treatment",
  "Percent No Treatment": "Percent of No Treatment",
  "Total ICS Patients": "Number of ICS Patients",
  "Number of Biologic Patients": "Number of Biologic Patients",
  "Total ICS Compliance < 240": "Number of ICS Compliance < 240",
  "Percent ICS Compliance < 240": "Percent of ICS Compliance < 240",
  "Total ICS Persistence < 360": "Number of ICS Persistence < 360",
  "Percent ICS Persistence < 360": "Percent of ICS Persistence < 360",
  "Total ICS High Steroid Usage": "Number of ICS High Steroid Usage",
  "Percent ICS High Steroid Usage": "Percent of ICS High Steroid Usage",
  "Total ICS Exacerbations": "Number of ICS Exacerbation",
  "Total ICS Exacerbation Failed Escalation": "Number of ICS Exacerbation Failed Escalation",
  "Percent ICS Exacerbation Failed Escalation": "Percent of ICS Exacerbation Failed Escalation",
  "Total ICS Escalation Delay": "Number of ICS Escalation Delay",
  "Percent ICS Escalation Delay": "Percent of ICS Escalation Delay",
  "Total ICS-LABA Patients": "Number of ICS-LABA Patients",
  "Total ICS-LABA Compliance < 240": "Number of ICS-LABA Compliance < 240",
  "Percent ICS-LABA Compliance < 240": "Percent of ICS-LABA Compliance < 240",
  "Total ICS-LABA Persistence < 360": "Number of ICS-LABA Persistence < 360",
  "Percent ICS-LABA Persistence < 360": "Percent of ICS-LABA Persistence < 360",
  "Total ICS-LABA High Steroid Usage": "Number of ICS-LABA High Steroid Usage",
  "Percent ICS-LABA High Steroid Usage": "Percent of ICS-LABA High Steroid Usage",
  "Total ICS-LABA Exacerbations": "Number of ICS-LABA Exacerbation",
  "Total ICS-LABA Exacerbation Failed Escalation": "Number of ICS-LABA Exacerbation Failed Escalation",
  "Percent ICS-LABA Exacerbation Failed Escalation": "Percent of ICS-LABA Exacerbation Failed Escalation",
  "Total ICS-LABA Escalation Delay": "Number of ICS-LABA Escalation Delay",
  "Percent ICS-LABA Escalation Delay": "Percent of ICS-LABA Escalation Delay",
  "Total ICS-LABA LAMA Patients": "Number of ICS-LABA LAMA Patients",
  "Total ICS-LABA LAMA Compliance < 240": "Number of ICS-LABA LAMA Compliance < 240",
  "Percent ICS-LABA LAMA Compliance < 240": "Percent of ICS-LABA LAMA Compliance < 240",
  "Total ICS-LABA LAMA Persistence < 360": "Number of ICS-LABA LAMA Persistence < 360",
  "Percent ICS-LABA LAMA Persistence < 360": "Percent of ICS-LABA LAMA Persistence < 360",
  "Total ICS-LABA With LAMA": "Number of ICS-LABA Patients with LAMA",
  "Percent ICS-LABA With LAMA": "Percent of ICS-LABA Patients with LAMA",
  // Pass-through mappings (same key in both formats)
  "Number of ICS-LABA >900mg/year steroids": "Number of ICS-LABA >900mg/year steroids",
  "Percent of ICS-LABA >900mg/year steroids": "Percent of ICS-LABA >900mg/year steroids",
  "Number of ICS-LABA High Steroid Usage with ER visit": "Number of ICS-LABA High Steroid Usage with ER visit",
  "Percent of ICS-LABA High Steroid Usage with ER visit": "Percent of ICS-LABA High Steroid Usage with ER visit",
  "Number of ICS-LABA High Steroid Usage without ER visit": "Number of ICS-LABA High Steroid Usage without ER visit",
  "Percent of ICS-LABA High Steroid Usage without ER visit": "Percent of ICS-LABA High Steroid Usage without ER visit",
  "Number of Failure To Escalate With 3 Exacerbations": "Number of Failure To Escalate With 3 Exacerbations",
  "Percent of Failure To Escalate With 3 Exacerbations": "Percent of Failure To Escalate With 3 Exacerbations",
  "Number of Biologic Before Triple": "Number of Biologic Before Triple",
  "Percent of Biologic Before Triple": "Percent of Biologic Before Triple",
  "Number of Exacerbation Without Spirometry": "Number of Exacerbation Without Spirometry",
  "Percent of Exacerbation Without Spirometry": "Percent of Exacerbation Without Spirometry",
};

// Generate inverted map labels programmatically
export const invertedMapLabels = Object.fromEntries(
  Object.entries(mapLabels).map(([key, value]) => [value, key])
);

// Generate labels matrix programmatically from base definitions
const labelsMatrixKeys = [
  "Number of No Spirometry",
  "Number of Exacerbation Without Spirometry",
  "Number of No EOS Testing",
  "Number of No Treatment",
  "Number of ICS High Steroid Usage",
  "Number of ICS Exacerbation Failed Escalation",
  "Number of ICS Escalation Delay",
  "Number of ICS-LABA High Steroid Usage",
  "Number of Failure To Escalate With 3 Exacerbations",
  "Number of ICS-LABA Exacerbation Failed Escalation",
  "Number of ICS-LABA >900mg/year steroids",
  "Number of ICS-LABA High Steroid Usage with ER visit",
  "Number of ICS-LABA High Steroid Usage without ER visit",
  "Number of ICS-LABA Escalation Delay",
  "Number of ICS-LABA Patients with LAMA",
  "Number of Biologic Before Triple",
  "Number of ICS Persistence < 360",
  "Number of ICS Compliance < 240",
  "Number of ICS-LABA Persistence < 360",
  "Number of ICS-LABA Compliance < 240",
  "Number of ICS-LABA LAMA Persistence < 360",
  "Number of ICS-LABA LAMA Compliance < 240",
];

export const labelsMatrix = Object.fromEntries(
  labelsMatrixKeys.map((key) => [
    key,
    {
      Number: key,
      Percent: key.replace("Number of", "Percent of"),
    },
  ])
);

// ============================================
// EXCLUDED LABELS
// ============================================

export const excludedLabels = labelsMatrixKeys;

// ============================================
// FILTER COLORS
// ============================================

export const filterColors = {
  "Total Asthma Patients": "#5b0c0f",
  "Total No Spirometry": "#2b262c",
  "Percent No Spirometry": "#31c2b2",
  "Total No EOS Testing": "#244cef",
  "Percent No EOS Testing": "#5d6cdd",
  "Total No Treatment": "#c77542",
  "Percent No Treatment": "#bc1a14",
  "Total ICS Patients": "#d5eba5",
  "Total ICS Compliance < 240": "#53249c",
  "Percent ICS Compliance < 240": "#1ac807",
  "Total ICS Persistence < 360": "#0512d2",
  "Percent ICS Persistence < 360": "#b04b3c",
  "Total ICS High Steroid Usage": "#72025e",
  "Percent ICS High Steroid Usage": "#56a44f",
  "Total ICS Exacerbations": "#298f07",
  "Total ICS Exacerbation Failed Escalation": "#8bdae5",
  "Percent ICS Exacerbation Failed Escalation": "#8fa0d3",
  "Total ICS Escalation Delay": "#8cacc3",
  "Percent ICS Escalation Delay": "#cb136d",
  "Total ICS-LABA Patients": "#1b6454",
  "Total ICS-LABA Compliance < 240": "#8340d4",
  "Percent ICS-LABA Compliance < 240": "#13e058",
  "Total ICS-LABA Persistence < 360": "#90bbc3",
  "Percent ICS-LABA Persistence < 360": "#bd3ef0",
  "Total ICS-LABA High Steroid Usage": "#5df04f",
  "Percent ICS-LABA High Steroid Usage": "#66a6f1",
  "Total ICS-LABA Exacerbations": "#8fadb4",
  "Total ICS-LABA Exacerbation Failed Escalation": "#827479",
  "Percent ICS-LABA Exacerbation Failed Escalation": "#d19fa4",
  "Total ICS-LABA Escalation Delay": "#d0e371",
  "Percent ICS-LABA Escalation Delay": "#17f36c",
  "Total ICS-LABA LAMA Patients": "#09af84",
  "Total ICS-LABA LAMA Compliance < 240": "#751423",
  "Percent ICS-LABA LAMA Compliance < 240": "#3530b8",
  "Total ICS-LABA LAMA Persistence < 360": "#05950f",
  "Percent ICS-LABA LAMA Persistence < 360": "#27d953",
  "Total ICS-LABA With LAMA": "#bddc13",
  "Percent ICS-LABA With LAMA": "#82820d",
};

// ============================================
// PROFILE VISUALIZATION
// ============================================

export const SHORT_LABELS = {
  "Number of No Spirometry": "No spirometry",
  "Number of Exacerbation Without Spirometry": "Exacerb. w/o spirometry",
  "Number of No EOS Testing": "No EOS testing",
  "Number of No Treatment": "No treatment",
  "Number of ICS Exacerbation Failed Escalation": "Failed escalation",
  "Number of ICS High Steroid Usage": "Excessive steroids",
  "Number of ICS Escalation Delay": "Escalation delay",
  "Number of ICS Persistence < 360": "Non-adherence (persistence)",
  "Number of ICS Compliance < 240": "Non-adherence (compliance)",
  "Number of ICS-LABA Exacerbation Failed Escalation": "Excessive steroids",
  "Number of ICS-LABA High Steroid Usage": "Failed escalation",
  "Number of Failure To Escalate With 3 Exacerbations": "Failed escal. 3+ exacerb.",
  "Number of ICS-LABA >900mg/year steroids": ">900mg/yr steroids",
  "Number of ICS-LABA High Steroid Usage with ER visit": "Excess. steroids <5mg OCS",
  "Number of ICS-LABA High Steroid Usage without ER visit": "Excess. steroids >5mg OCS",
  "Number of ICS-LABA Escalation Delay": "Escalation delay",
  "Number of ICS-LABA Persistence < 360": "Non-adherence (persistence)",
  "Number of ICS-LABA Compliance < 240": "Non-adherence (compliance)",
  "Number of ICS-LABA LAMA Persistence < 360": "Non-adherence (persistence)",
  "Number of ICS-LABA LAMA Compliance < 240": "Non-adherence (compliance)",
  "Number of ICS-LABA Patients with LAMA": "Open triple therapy",
  "Number of Biologic Before Triple": "Biologic before triple",
};

export const PROFILE_SECTIONS = [
  {
    title: "Diagnosis & Investigation",
    denominatorKey: "Number of Asthma Patients",
    denominatorLabel: "asthma patients",
    metrics: [
      "Number of No Spirometry",
      "Number of Exacerbation Without Spirometry",
      "Number of No EOS Testing",
      "Number of No Treatment",
    ],
  },
  {
    title: "Single Therapy (ICS)",
    denominatorKey: "Number of ICS Patients",
    denominatorLabel: "ICS patients",
    metrics: [
      "Number of ICS Exacerbation Failed Escalation",
      "Number of ICS High Steroid Usage",
      "Number of ICS Escalation Delay",
      "Number of ICS Persistence < 360",
      "Number of ICS Compliance < 240",
    ],
  },
  {
    title: "Double Therapy (ICS-LABA)",
    denominatorKey: "Number of ICS-LABA Patients",
    denominatorLabel: "ICS-LABA patients",
    metrics: [
      "Number of ICS-LABA High Steroid Usage",
      "Number of ICS-LABA Exacerbation Failed Escalation",
      "Number of Failure To Escalate With 3 Exacerbations",
      "Number of ICS-LABA >900mg/year steroids",
      "Number of ICS-LABA Escalation Delay",
      "Number of ICS-LABA Persistence < 360",
      "Number of ICS-LABA Compliance < 240",
    ],
  },
  {
    title: "Advanced Therapy (Triple Therapy or Biologic)",
    denominatorKey: "Number of ICS-LABA LAMA Patients",
    denominatorLabel: "ICS-LABA-LAMA patients",
    extraDenominators: ["Number of Biologic Patients"],
    metrics: [
      "Number of ICS-LABA LAMA Persistence < 360",
      "Number of ICS-LABA LAMA Compliance < 240",
      "Number of ICS-LABA Patients with LAMA",
      "Number of Biologic Before Triple",
    ],
  },
];

// ============================================
// CHART CONFIGURATIONS
// ============================================

export const mapBarCharts = {
  chart1: [
    "Total Asthma Patients",
    "Total No Spirometry",
    "Number of Exacerbation Without Spirometry",
    "Total No EOS Testing",
    "Total No Treatment",
  ],
  chart2: ["Total Asthma Patients", "Total No Treatment"],
  chart3: [
    "Total ICS Patients",
    "Total ICS Exacerbation Failed Escalation",
    "Total ICS High Steroid Usage",
    "Total ICS Escalation Delay",
    "Total ICS Persistence < 360",
    "Total ICS Compliance < 240",
  ],
  chart4: [
    "Total ICS-LABA Patients",
    "Total ICS-LABA Exacerbation Failed Escalation",
    "Total ICS-LABA High Steroid Usage",
    "Number of Failure To Escalate With 3 Exacerbations",
    "Number of ICS-LABA >900mg/year steroids",
    "Number of ICS-LABA High Steroid Usage with ER visit",
    "Number of ICS-LABA High Steroid Usage without ER visit",
    "Total ICS-LABA Escalation Delay",
    "Total ICS-LABA Persistence < 360",
    "Total ICS-LABA Compliance < 240",
  ],
  chart5: [
    "Total ICS-LABA LAMA Patients",
    "Number of Biologic Patients",
    "Total ICS-LABA LAMA Persistence < 360",
    "Total ICS-LABA LAMA Compliance < 240",
    "Total ICS-LABA With LAMA",
    "Number of Biologic Before Triple",
  ],
};
